import { Box, Button, Choicebox, FormGroup, FormParagraph, TextArea } from '@ksyos/design-system';
import { Fragment, useEffect, useState } from 'react';
import type {
    IExtraAdditionalExaminations,
    IOrthOProcedureCustomControlData,
} from '../../../../../common/interfaces/ophthalmology/orthO/orthOProcedureCustomControlData';
import type { IOrthOV1ProcedureExaminationsProps } from './OrthOV1ProcedureExaminations';
import { getValidationErrorTranslation } from '../../../../utils/getValidationErrorTranslation';
import { OrthOExaminationOrder } from './orthOExaminationOrder';
import { OrthOV1Procedure30cm6mMeasurements } from './OrthOV1Procedure30cm6mMeasurements';
import { orthOV1ProcedureInitializeCustomControlData } from './orthOV1ProcedureInitializeCustomControlData';
import { OrthOV1ProcedureOdOsOdsFindingsTable } from './OrthOV1ProcedureOdOsOdsFindingsTable';
import { ReadOrEditInputField } from './ReadOrEditInputField';

const extraAdditionalExaminations = [
    'Glazen van Bagolini',
    'Prismafusie horizontaal',
    'Prismafusie verticaal',
    'Accommodatie',
    'Pupilreacties',
    'Extra onderzoek',
] as const;

type IOrthOExtraAdditionalExaminations = (typeof extraAdditionalExaminations)[number];

export function OrthOV1ProcedureSelectedExtraAdditionalExaminations(props: IOrthOV1ProcedureExaminationsProps) {
    const [selectedExtraAdditionalExaminations, setSelectedExtraAdditionalExaminations] = useState<
        IOrthOExtraAdditionalExaminations[]
    >([]);
    const [extraExaminations, setExtraExaminations] = useState<IExtraAdditionalExaminations[]>([
        { name: '', findings: '' },
    ]);

    useEffect(() => {
        updateSelectedExtraAdditionalExaminations();
    }, []);

    return (
        <>
            {!props.isReadOnly && (
                <FormGroup label="Onderzoek toevoegen">
                    <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        gap={3}
                    >
                        {extraAdditionalExaminations.map((examination, index) => {
                            return (
                                <Choicebox
                                    checked={selectedExtraAdditionalExaminations.includes(examination)}
                                    key={`${examination}-${index}`}
                                    label={examination}
                                    name={examination}
                                    onChange={() => addOrRemoveExamination(examination)}
                                    tabIndex={OrthOExaminationOrder.notFocusable}
                                />
                            );
                        })}
                    </Box>
                </FormGroup>
            )}

            {selectedExtraAdditionalExaminations.includes('Glazen van Bagolini') && (
                <ReadOrEditInputField
                    {...props}
                    error={props.errors?.get('customControlData.glazen_bagolini')}
                    fieldKey="glazen_bagolini"
                    hasErrors={props.errors?.has('customControlData.glazen_bagolini')}
                    isRequired={false}
                    label="orthOProcedure.glasses_bagolini"
                    placeholder="Bevindingen..."
                    tabIndex={OrthOExaminationOrder.extraAdditionalExaminations}
                    value={props.customControlData.glazen_bagolini}
                />
            )}

            {selectedExtraAdditionalExaminations.includes('Prismafusie horizontaal') && (
                <OrthOV1Procedure30cm6mMeasurements
                    errors={props.errors}
                    field="prismafusie_horizontaal"
                    isReadOnly={props.isReadOnly}
                    label="Prismafusie horizontaal"
                    tabIndex={OrthOExaminationOrder.extraAdditionalExaminations}
                    value_30={props.customControlData.prismafusie_horizontaal_30cm}
                    value_6={props.customControlData.prismafusie_horizontaal_6m}
                />
            )}

            {selectedExtraAdditionalExaminations.includes('Prismafusie verticaal') && (
                <OrthOV1Procedure30cm6mMeasurements
                    errors={props.errors}
                    field="prismafusie_verticaal"
                    isReadOnly={props.isReadOnly}
                    label="Prismafusie verticaal"
                    tabIndex={OrthOExaminationOrder.extraAdditionalExaminations}
                    value_30={props.customControlData.prismafusie_verticaal_30cm}
                    value_6={props.customControlData.prismafusie_verticaal_6m}
                />
            )}

            {selectedExtraAdditionalExaminations.includes('Accommodatie') && (
                <OrthOV1ProcedureOdOsOdsFindingsTable
                    errors={props.errors}
                    field="accommodatie"
                    isReadOnly={props.isReadOnly}
                    label="Accommodatie"
                    tabIndexOd={OrthOExaminationOrder.extraAdditionalExaminationOd}
                    tabIndexOds={OrthOExaminationOrder.extraAdditionalExaminationOds}
                    tabIndexOs={OrthOExaminationOrder.extraAdditionalExaminationOs}
                    value_od={props.customControlData.accommodatie_od}
                    value_ods={props.customControlData.accommodatie_ods}
                    value_os={props.customControlData.accommodatie_os}
                />
            )}

            {selectedExtraAdditionalExaminations.includes('Pupilreacties') && (
                <ReadOrEditInputField
                    {...props}
                    error={props.errors?.get('customControlData.pupil_reacties')}
                    fieldKey="pupil_reacties"
                    hasErrors={props.errors?.has('customControlData.pupil_reacties')}
                    isRequired={false}
                    label="orthOProcedure.pupil_reactions"
                    placeholder="Bevindingen..."
                    tabIndex={OrthOExaminationOrder.extraAdditionalLast}
                    value={props.customControlData.pupil_reacties}
                />
            )}

            {selectedExtraAdditionalExaminations.includes('Extra onderzoek') && (
                <>
                    {!props.isReadOnly && (
                        <>
                            {extraExaminations.map((examination, examIndex) => {
                                const hasErrors =
                                    props.errors.has(
                                        `customControlData.extra_aanvullende_onderzoeken.${examIndex}.name`
                                    ) ||
                                    props.errors.has(
                                        `customControlData.extra_aanvullende_onderzoeken.${examIndex}.bevinding`
                                    );

                                return (
                                    <Fragment key={`${examination}-${examIndex}`}>
                                        <FormGroup
                                            hasErrors={hasErrors}
                                            label={`Extra onderzoek ${examIndex + 1}`}
                                        >
                                            <TextArea
                                                className={`automation-extraOnderzoekName${examIndex}`}
                                                error={getValidationErrorTranslation(
                                                    props.errors.get(
                                                        `customControlData.extra_aanvullende_onderzoeken.${examIndex}.name`
                                                    )
                                                )}
                                                onChange={async (event) => {
                                                    const updatedExaminations = extraExaminations.map((exam, index) => {
                                                        if (index === examIndex) {
                                                            return { ...exam, name: event.target.value || '' };
                                                        } else {
                                                            return exam;
                                                        }
                                                    });
                                                    setExtraExaminations(updatedExaminations);
                                                    await props.handleChangeCustomControl({
                                                        extra_aanvullende_onderzoeken: updatedExaminations,
                                                    });
                                                    props.validateOneCustomControl(
                                                        'extra_aanvullende_onderzoeken',
                                                        examIndex
                                                    );
                                                }}
                                                placeholder={'Naam extra onderzoek...'}
                                                tabIndex={OrthOExaminationOrder.extraAdditionalLast}
                                                value={extraExaminations[examIndex].name}
                                            />
                                            <TextArea
                                                className={`automation-extraOnderzoekFindings${examIndex} ${
                                                    props.errors.has(
                                                        `customControlData.extra_aanvullende_onderzoeken.${examIndex}.findings`
                                                    )
                                                        ? 'has-danger'
                                                        : ''
                                                }`}
                                                error={getValidationErrorTranslation(
                                                    props.errors.get(
                                                        `customControlData.extra_aanvullende_onderzoeken.${examIndex}.findings`
                                                    )
                                                )}
                                                onChange={async (event) => {
                                                    const updatedExaminations = extraExaminations.map((exam, index) => {
                                                        if (index === examIndex) {
                                                            return { ...exam, findings: event.target.value || '' };
                                                        } else {
                                                            return exam;
                                                        }
                                                    });
                                                    setExtraExaminations(updatedExaminations);
                                                    await props.handleChangeCustomControl({
                                                        extra_aanvullende_onderzoeken: updatedExaminations,
                                                    });
                                                    props.validateOneCustomControl(
                                                        'extra_aanvullende_onderzoeken',
                                                        examIndex
                                                    );
                                                }}
                                                placeholder={'Bevindingen...'}
                                                tabIndex={OrthOExaminationOrder.extraAdditionalLast}
                                                value={extraExaminations[examIndex].findings}
                                            />
                                        </FormGroup>
                                    </Fragment>
                                );
                            })}

                            <FormGroup hasErrors={props.errors?.has('customControlData.extra_aanvullende_onderzoeken')}>
                                <Button
                                    iconLeft="add"
                                    onClick={() =>
                                        setExtraExaminations([...extraExaminations, { name: '', findings: '' }])
                                    }
                                    variant="link"
                                >
                                    Nog een extra onderzoek toevoegen
                                </Button>
                            </FormGroup>
                        </>
                    )}

                    {props.isReadOnly && (
                        <>
                            {props.customControlData.extra_aanvullende_onderzoeken.map(
                                (onderzoek: IExtraAdditionalExaminations) => (
                                    <FormGroup
                                        key={`${onderzoek.name}-${onderzoek.findings}`}
                                        label={onderzoek.name}
                                    >
                                        <FormParagraph>{onderzoek.findings}</FormParagraph>
                                    </FormGroup>
                                )
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );

    function updateSelectedExtraAdditionalExaminations() {
        const additionalExaminationsToSet: IOrthOExtraAdditionalExaminations[] = [
            ...selectedExtraAdditionalExaminations,
        ];
        if (
            props.customControlData.extra_aanvullende_onderzoeken.length &&
            !selectedExtraAdditionalExaminations.includes('Extra onderzoek')
        ) {
            additionalExaminationsToSet.push('Extra onderzoek');
            setExtraExaminations(props.customControlData.extra_aanvullende_onderzoeken);
        }
        if (
            props.customControlData.glazen_bagolini &&
            !selectedExtraAdditionalExaminations.includes('Glazen van Bagolini')
        ) {
            additionalExaminationsToSet.push('Glazen van Bagolini');
        }
        if (
            (props.customControlData.prismafusie_horizontaal_30cm ||
                props.customControlData.prismafusie_horizontaal_6m) &&
            !selectedExtraAdditionalExaminations.includes('Prismafusie horizontaal')
        ) {
            additionalExaminationsToSet.push('Prismafusie horizontaal');
        }
        if (
            (props.customControlData.prismafusie_verticaal_30cm || props.customControlData.prismafusie_verticaal_6m) &&
            !selectedExtraAdditionalExaminations.includes('Prismafusie verticaal')
        ) {
            additionalExaminationsToSet.push('Prismafusie verticaal');
        }
        if (
            (props.customControlData.accommodatie_od ||
                props.customControlData.accommodatie_os ||
                props.customControlData.accommodatie_ods) &&
            !selectedExtraAdditionalExaminations.includes('Accommodatie')
        ) {
            additionalExaminationsToSet.push('Accommodatie');
        }
        if (props.customControlData.pupil_reacties && !selectedExtraAdditionalExaminations.includes('Pupilreacties')) {
            additionalExaminationsToSet.push('Pupilreacties');
        }
        setSelectedExtraAdditionalExaminations(additionalExaminationsToSet);
    }

    async function addOrRemoveExamination(examination: IOrthOExtraAdditionalExaminations) {
        if (!props.isReadOnly) {
            if (selectedExtraAdditionalExaminations.includes(examination)) {
                const examinationToFieldMapper = {
                    'Glazen van Bagolini': ['glazen_bagolini'],
                    'Prismafusie horizontaal': ['prismafusie_horizontaal_30cm', 'prismafusie_horizontaal_6m'],
                    'Prismafusie verticaal': ['prismafusie_verticaal_30cm', 'prismafusie_verticaal_6m'],
                    Accommodatie: ['accommodatie_od', 'accommodatie_os', 'accommodatie_ods'],
                    Pupilreacties: ['pupilreacties'],
                    'Extra onderzoek': ['extra_aanvullende_onderzoeken'],
                };

                const initialFieldValues = orthOV1ProcedureInitializeCustomControlData();

                for (const field of examinationToFieldMapper[examination]) {
                    props.handleChangeCustomControl({
                        [field]: initialFieldValues[field as keyof IOrthOProcedureCustomControlData],
                    });
                }
                setSelectedExtraAdditionalExaminations([
                    ...selectedExtraAdditionalExaminations.filter((selectedExam) => selectedExam !== examination),
                ]);
            } else {
                setSelectedExtraAdditionalExaminations([...selectedExtraAdditionalExaminations, examination]);
            }
        }
    }
}
